import React, { useEffect } from "react";
import { Card, Form, Space, Row, Col, InputNumber, Divider } from "antd";
import _ from "lodash";

import TechBaseText from "DisplayCoreV2/Text";
import TechBaseButton from "DisplayCoreV2/Button";
import { requireRule } from "utils/rules";

const InstallmentFields = [
  {
    title: <TechBaseText content="{{NO_OF_INSTALLMENT}}" />,
    name: ["lot", "loanDetails", "installmentMonth"],
    min: 2
  },
  {
    title: <TechBaseText content="{{SUM_PER_INSTALLMENT}}" />,
    name: ["lot", "loanDetails", "sumPerInstallment"]
  },
  {
    title: <TechBaseText content="{{FIRST_INSTALLMENT}}" />,
    name: ["lot", "loanDetails", "firstInstallment"],
    disabled: true,
    transform: (value) => Number(value) // Ensure it is a number
  }
];


const Step2 = (props) => {
  const { selectedSlot, bookingOrder, onNext, onBack, formData, salesType } = props;
  const [form] = Form.useForm();
  const unitPrice = Form.useWatch(["lot", "unitPrice"], form);
  const maintenanceFee = Form.useWatch(["lot", "maintenanceFee"], form);
  const unitDiscount = Form.useWatch(["lot", "unitDiscount"], form);
  const balanceAmount = Form.useWatch("balance_amount", form);
  const downpayment = Form.useWatch(["lot", "downpayment"], form);
  const installmentMonth = Form.useWatch(["lot", "loanDetails", "installmentMonth"], form);
  const sumPerInstallment = Form.useWatch(["lot", "loanDetails", "sumPerInstallment"], form);

  const DetailFields = [
    { title: <TechBaseText content="{{UNIT_PRICE}}" />, name: ["lot", "unitPrice"] },
    { title: <TechBaseText content="{{MAINTENANCE_FEE}}" />, name: ["lot", "maintenanceFee"] },
    ...(props.isBurial
      ? [
        {
          title: <TechBaseText content="{{BACKWALL_AND_WALKWAY}}" />,
          name: ["lot", "backWallAmount"],
        },
      ]
      : []), // This ensures the field is only included when isBurial is true
    { title: <TechBaseText content="{{TOTAL}}" />, name: "total", disabled: true },
    { title: <TechBaseText content="{{UNIT_DISCOUNT}}" />, name: ["lot", "unitDiscount"] },
    { title: <TechBaseText content="{{BALANCE_PAYABLE}}" />, name: "balance_payable", disabled: true },
    { title: <TechBaseText content="{{DOWNPAYMENT}}" />, name: ["lot", "downpayment"], bold: true },
    { title: <TechBaseText content="{{BALANCE_AMOUNT}}" />, name: "balance_amount", disabled: true },
    { title: <TechBaseText content="{{BOOKING_FEE_RECEIVED}}" />, name: "booking_fee_received", disabled: true },
  ];





  // Ensure initial values in Form
  useEffect(() => {
    if (!props.isBurial) {
      form.setFieldsValue({
        lot: {
          ...form.getFieldValue("lot"),
          backWallAmount: 0, // Explicitly setting it to 0 for niche lots
        },
      });
    }
  }, [props.isBurial]);

  const backWallAmount = Form.useWatch(["lot", "backWallAmount"], form); // ✅ Track changes in `backWallAmount`

  useEffect(() => {
    let total = 0,
      payableBalance = 0,
      balanceAmount = 0,
      firstInstallment = 0;

    // ✅ Ensure all values are numbers
    const price = unitPrice || 0;
    const fee = maintenanceFee || 0;
    const discount = unitDiscount || 0;
    const downPay = downpayment || 0;
    const backWall = props.isBurial ? (backWallAmount || 0) : 0; // Only include backWallAmount for burial

    // ✅ Calculate Total
    total = price + fee + backWall;

    // ✅ Calculate Balance Payable
    payableBalance = total - discount;

    // ✅ Calculate Balance Amount
    balanceAmount = payableBalance - downPay; // No Math.max() to allow negatives



    // ✅ Calculate First Installment
    const months = installmentMonth || 1; // Default to 1
    const installmentSum = sumPerInstallment || 0;
    firstInstallment = balanceAmount - (months > 1 ? (months - 1) * installmentSum : 0);


    // ✅ Update Form Fields
    form.setFieldsValue({
      total: total.toFixed(2), // Ensure 2 decimal places
      balance_payable: payableBalance.toFixed(2),
      balance_amount: balanceAmount.toFixed(2),
    });
    form.setFieldValue(["lot", "loanDetails", "firstInstallment"], Number(firstInstallment.toFixed(2)) || 0);



  }, [
    unitPrice,
    maintenanceFee,
    unitDiscount,
    downpayment,
    installmentMonth,
    sumPerInstallment,
    props.isBurial,
    backWallAmount
  ]); // ✅ All dependencies tracked properly

  const onFinish = (values) => {
    if (values.lot.loanDetails) {
      values.lot.loanDetails.firstInstallment = Number(values.lot.loanDetails.firstInstallment || 0);
    }
    onNext(values);
  };



  return (
    <Form
      onFinish={onFinish}
      layout="vertical"
      form={form}
      initialValues={{
        purchaser_type: "existing",
        nationality: 127,
        mobile_prefix: "60",
        mobile_prefix_2: "60",
        identity_type: "ic",
        gender: "male",
        country: 127,
        state: 9,
        mailing_country: 127,
        mailing_state: 9,
        booking_fee_received: bookingOrder?.Price ?? bookingOrder?.TotalPrice ?? 0,
        lot: {
          unitPrice: bookingOrder?.AgreedPrice ?? (salesType === "FSP" ? selectedSlot.Price : selectedSlot.ReferencePrice),
          maintenanceFee: salesType === "FSP" ? 0 : selectedSlot.ReferenceContinuityFee,
          unitDiscount: 0,
          downpayment: bookingOrder?.Price ?? bookingOrder?.TotalPrice ?? 0,
          backWallAmount: 0, //IF LOT IS NICHE, BE WILL CHANGE THIS TO 0. IF LOT IS BURIAL YOU CAN PUT 0 OR HIGHER.
          loanDetails: {
            installmentMonth: 2,
            sumPerInstallment: 1
          }
        },
        ...formData
      }}
    >
      <Row className="w-100 d-block ">
        <Card className="mb-3">
          {salesType === "FSP" ? (
            <div className="lead">
              <TechBaseText content="{{SELECTED_FSP}}" />: {selectedSlot.ProductName}
            </div>
          ) : (
            <div className="lead">
              <TechBaseText content="{{LOT_NO}}" />: {selectedSlot.UnitID}
            </div>
          )}
        </Card>
        <Card className="mb-3">
          <div className="lead">
            <TechBaseText content="{{PAYMENT_DETAILS}}" />
          </div>
          <>
            <Row gutter={32}>
              {DetailFields.map((field, index) => (
                <Col xs={24} md={12} key={index}>
                  <span style={{ color: "#ff0000" }}>* </span>
                  {field.bold ? <strong>{field.title}</strong> : field.title}
                  <div>
                    <Form.Item
                      className="mt-1"
                      name={field.name}
                      rules={[
                        requireRule,
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (value < 0) {
                              return Promise.reject(new Error("Value should not be negative"));
                            }
                            return Promise.resolve();
                          }
                        })
                      ]}
                    >
                      <InputNumber className="w-100" disabled={field.disabled} min={field.min} />
                    </Form.Item>
                  </div>
                </Col>
              ))}
            </Row>

            {/* <Row gutter={32}>
                <Col xs={24} md={24}>
                <span style={{ color: "#ff0000" }}>* </span>
                {field.bold ? <strong>{field.title}</strong> : field.title}
                <div>
                  <Form.Item
                  className="mt-1"
                  name={field.name}
                  rules={[
                    requireRule,
                    ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (value < 0) {
                      return Promise.reject(new Error("Value should not be negative"));
                      }
                      return Promise.resolve();
                    }
                    })
                  ]}
                  >
                  <InputNumber className="w-100" disabled={field.disabled} min={field.min} />
                  </Form.Item>
                </div>
                </Col>
              </Row> */}
            {/* <Divider className="my-3" />
              <Row gutter={32}>
                <Col xs={24} md={12}>
                  <span style={{ color: "#ff0000" }}>*</span>
                  {field.bold ? <strong>{field.title}</strong> : field.title}
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    className="m-0"
                    name={field.name}
                    rules={[
                      requireRule,
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (value < 0) {
                            return Promise.reject(new Error("Value should not be negative"));
                          }
                          return Promise.resolve();
                        }
                      }),
                      ...(_.isEqual(field.name, ["lot", "downpayment"])
                        ? [
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                if (value < getFieldValue("booking_fee_received")) {
                                  return Promise.reject(
                                    new Error("Downpayment should not be less than booking fee received")
                                  );
                                }
                                return Promise.resolve();
                              }
                            })
                          ]
                        : [])
                    ]}
                  >
                    <InputNumber className="w-100" disabled={field.disabled} />
                  </Form.Item>
                </Col>
              </Row> */}
          </>
        </Card>
        {balanceAmount > 0 && (
          <Card className="mb-3">
            <Space>
              <div className="lead">
                <TechBaseText content="{{INSTALLMENT_DETAILS}}" />
              </div>
            </Space>
            <>
              <Row gutter={32}>
                {InstallmentFields.map((field, index) => (
                  <Col xs={24} md={12} key={index}>
                    <span style={{ color: "#ff0000" }}>* </span>
                    {field.title}
                    <div>
                      <Form.Item
                        className="mt-1"
                        name={field.name}
                        rules={[
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              if (value === undefined || value < 0) {
                                return Promise.reject(new Error("Value cannot be negative"));
                              }
                              return Promise.resolve();
                            }
                          })
                        ]}
                      >
                        <InputNumber className="w-100" disabled={field.disabled} min={0} />
                      </Form.Item>

                    </div>
                  </Col>
                ))}
              </Row>


              {/* <Row gutter={32}>
                  <Col xs={24} md={24}>
                  <span style={{ color: "#ff0000" }}>* </span>
                  {field.title}
                  <div>
                    <Form.Item
                    className="mt-1"
                    name={field.name}
                    rules={[
                      requireRule,
                      ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (value < 0) {
                        return Promise.reject(new Error("Value should not be negative"));
                        }
                        return Promise.resolve();
                      }
                      })
                    ]}
                    >
                    <InputNumber className="w-100" disabled={field.disabled} min={field.min} />
                    </Form.Item>
                  </div>
                  </Col>
                </Row> */}
              {/* <Divider className="my-3" />
                <Row gutter={32}>
                  <Col xs={24} md={12}>
                    <span style={{ color: "#ff0000" }}>*</span>
                    {field.bold ? <strong>{field.title}</strong> : field.title}
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      className="m-0"
                      name={field.name}
                      rules={[
                        requireRule,
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (value < 0) {
                              return Promise.reject(new Error("Value should not be negative"));
                            }
                            return Promise.resolve();
                          }
                        })
                      ]}
                    >
                      <InputNumber className="w-100" disabled={field.disabled} min={field.min} />
                    </Form.Item>
                  </Col>
                </Row> */}
            </>
            {/* <Divider className="my-3" />
          <Row gutter={32}>
            <Col xs={24} md={12}>
              <span style={{ color: "#ff0000" }}>*</span>Sold Condition
            </Col>
            <Col xs={24} md={12}>
              <Form.Item className="m-0" name="sold_condition" rules={requiredRules}>
                <Select size="large" options={needsOptions} />
              </Form.Item>
            </Col>
          </Row> */}
          </Card>
        )}
        {balanceAmount < 0 && (
          <div style={{ color: "red", marginBottom: "10px" }}>
            <TechBaseText content="{{BALANCE_AMOUNT_CANNOT_BE_NEGATIVE_PLEASE_ADJUST_THE_DOWNPAYMENT}}" />
          </div>
        )}

        <Row className="w-100 d-flex footer-btn">
          <Col>
            <TechBaseButton onClick={() => onBack(form.getFieldsValue())}>
              <TechBaseText content="{{BACK}}" />
            </TechBaseButton>
          </Col>
          <Col>
            <TechBaseButton htmlType="submit" color="primary" disabled={balanceAmount < 0}>
              <TechBaseText content="{{NEXT}}" />
            </TechBaseButton>
          </Col>
        </Row>

      </Row>
    </Form>
  );
};

export default Step2;
